/**
 * Iter-151 · Phase 3b + 5b — Running Services Image Carousel.
 *
 * Runs continuously as a marquee. Defaults to the original 7 service
 * images; commercial views supply their own illustrations. Mounted on:
 *   • Commercial Services page — directly under the 12-industries grid
 *     (replaces the removed SPECIALTY FACILITY SERVICES packages block).
 *   • Multifamily page — directly under the Multifamily services block.
 */
import { useMemo } from 'react';

// The same 7 images used inside the old Services page carousel — real
// NECS photography. Duplicated in the marquee so the loop is seamless.
const SERVICE_SLIDES = [
  {
    title: 'Valet Trash & Recycling',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/69t216o6_1623.jpg',
    objectPosition: 'center 30%',
  },
  {
    title: 'Commercial Cleaning',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/7w7zt9ib_90872.png',
  },
  {
    title: 'Pressure Washing',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/egsed6f4_91776.png',
  },
  {
    title: 'Bulk Removal',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/8ggel5t8_92724.png',
  },
  {
    title: 'Property Appearance',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/fqpw6qbx_91777.png',
  },
  {
    title: 'Dumpster Treatment',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/o0yl93et_92743.png',
  },
  {
    title: 'Window Cleaning',
    image: 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/rbff15xp_2567.png',
  },
];

export default function ServicesRunningCarousel({ testId = 'services-running-carousel', slides = SERVICE_SLIDES }) {
  // Duplicate the array so the marquee loops without a visible seam.
  const items = useMemo(() => [...slides, ...slides], [slides]);

  return (
    <section className="mt-10" data-testid={testId}>
      <p
        className="text-[10px] uppercase tracking-[0.28em] font-bold text-center mb-4"
        style={{ color: '#C9A962' }}
        data-testid={`${testId}-heading`}
      >
        NECS · Services in Action
      </p>
      <div
        className="relative overflow-hidden py-2"
        style={{
          maskImage: 'linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)',
        }}
      >
        <div
          className="flex gap-4"
          style={{
            animation: 'necs-services-marquee 45s linear infinite',
            width: 'max-content',
          }}
        >
          {items.map((s, i) => (
            <div
              key={`${s.title}-${i}`}
              className="relative flex-shrink-0 rounded-lg overflow-hidden"
              style={{
                width: '320px',
                height: '200px',
                border: '1px solid #3f3f46',
              }}
              data-testid={`${testId}-item-${i}`}
            >
              <img
                src={s.image}
                alt={s.alt || s.title}
                className="w-full h-full object-cover"
                style={{ objectPosition: s.objectPosition, objectFit: s.objectFit }}
                loading="lazy"
                data-testid={`${testId}-image-${i}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <p
                  className="text-sm font-black uppercase tracking-widest text-white"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testId}-caption-${i}`}
                >
                  {s.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes necs-services-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
